import { MathUtils, Euler, Quaternion, Vector3, Scene } from "three";
import { IPresetAnimationOptionUnion, IComponentsById, ITuple3, IPresetAnimation, IPresetAnimationOrbitOptions, IComponentType, IFaceLandmarkGroup, ISceneComp, IScreenAnchorGroup, IScreenContent, ITrackingTypes, ITriggerTypes, ISpatialComponentUnion, IRoot } from "../component-data-structure";
import { ISetTransitionInput, Transition, IEasingTypes, getScreenRelativeComponentIdsByScreenContentId } from "../transitions";
import { getInitialEntityTransformValuesByIds } from "../utils/actions";
import { rotateAroundWorldAxis } from "../utils/animationPresets";
import { getAdjustedPosition, getAdjustedScale } from "../utils/general";
import { getScreenContentIdForScene } from "../utils/pure";

export type IPresetAnimationControllerOption = IPresetAnimationOptionUnion & { triggerType: ITriggerTypes, parentSceneId: string, componentId: string};

interface IPresetAnimationContollerSettingsInput {
    options?: IPresetAnimationControllerOption[];
    componentsById?: IComponentsById;
    scene?: Scene;  
}

interface IPresetAnimationControllerDesignerConstructorInput extends IPresetAnimationContollerSettingsInput {
    scene: Scene;
    debug?: boolean;
}

interface IPresetAnimationControllerJuteConstructorInput extends IPresetAnimationContollerSettingsInput {
    scene: Scene;
    debug?: boolean;
    threeEntitySelectorPrefix: string;
    mode: 'ar' | 'nonar';
    rootId: string;
    adjustSpatialPositions?: boolean;
}


interface ITransforms {
    position?: Vector3;
    scale?: Vector3;
    rotation?: Euler;
    quaternion?: Quaternion;
}

type ITransformTypes = 'rotation' | 'position' | 'scale';

interface IAnimationData {
    settings: ISetTransitionInput<number>,
    initialTransforms?: ITransforms[],
    pivot?: Vector3 | string, 
    positionIsOffset?: boolean,
    axis?: Vector3
    reusableQuaternion?: Quaternion;
    transformInnerGroup?: boolean;
    angle?: number;
    type: ITransformTypes | 'orbit';
    targetIds: string[];
    multiplier?: number;
    isOffset?: boolean;
    rotation?: ITuple3;
    position?: ITuple3;
    triggerType: ITriggerTypes;
    parentSceneId: string;
    componentId: string;
    animation: Transition<number>;
}

interface IResetInput {
    invokeOnFinishAll?: boolean,
    checkResetOnFinish?: boolean,
}

interface IInternalResetInput {
    animationDataArray?: IAnimationData[];
    checkResetOnFinish: boolean;
}

interface InitialTransformValues {
    scale: ITuple3;
    position: ITuple3;
    rotation: ITuple3;
}

type ICallback = () => unknown;

export class PresetAnimationController {
    private _animationData?: IAnimationData[] = [];
    private _componentsById?: IComponentsById;
    private _threeScene: Scene;
    private _anAnimationHasStarted = false;
    private _runningAnimationNumber = 0;
    private _debug = false;
    private _rotation360InRad = MathUtils.degToRad(360);
    private _onStartCbs: ICallback[] = [];
    private _onFinishAllCbs: ICallback[] = [];
    private _rootId?: string;
    private _mode: 'ar' | 'nonar' = 'ar';
    private _threeEntitySelectorPrefix = '';
    private _adjustSpatialPositions?: boolean;

    constructor(input: IPresetAnimationControllerJuteConstructorInput);
    constructor(input: IPresetAnimationControllerDesignerConstructorInput);
    constructor(input: IPresetAnimationControllerJuteConstructorInput & IPresetAnimationControllerDesignerConstructorInput) {
        const {options, componentsById, scene, threeEntitySelectorPrefix, debug, adjustSpatialPositions, mode} =  input;
        this._threeScene = scene;
        this._componentsById = componentsById;
        if (typeof mode !== 'undefined') {
            this._mode = mode;
        }
        this._threeEntitySelectorPrefix = threeEntitySelectorPrefix ?? this._threeEntitySelectorPrefix;
        //this._animationData = !!options ? this._getAnimationDataFromOptions(options) : this._animationData;
        this._debug = !!debug;
        this._adjustSpatialPositions = !!adjustSpatialPositions;
        if (this._debug) console.log('presetAnimationController: constructed', this._animationData);
    }

    set (input: IPresetAnimationContollerSettingsInput): PresetAnimationController {
        const {options, componentsById, scene} = input;
        this._componentsById = componentsById ?? this._componentsById;
        this._animationData = !!options ? this._getAnimationDataFromOptions(options) : this._animationData;
        this._threeScene = scene ?? this._threeScene;
        if (this._debug) console.log('presetAnimationController: set', this._animationData);
        return this;
    }

    reset(input?: IResetInput): PresetAnimationController {
        const { invokeOnFinishAll, checkResetOnFinish } = input ?? {};
        this._animationData?.forEach(({animation}) => animation.reset())
        this._runningAnimationNumber = 0;
        this._anAnimationHasStarted = false;
        this._resetAnimatedObjectsToInitialThreeValues({animationDataArray: this._animationData, checkResetOnFinish: !!checkResetOnFinish})
        if (invokeOnFinishAll) this._onFinishAll();
        if (this._debug) console.log('presetAnimationController: reset', this._animationData);
        this._onFinishAllCbs = [];
        this._onStartCbs = [];
        return this;
    }

    private _startAnimationsWithAnimationData = (data: IAnimationData[]) => {
        for (let i = 0; i < data.length; i++) {
            const animationData = data[i];
            const { settings, type, angle, initialTransforms, transformInnerGroup, targetIds, animation } = animationData;
            animation
                .reset()
                .set(settings)
                .onStart(() => {
                    if (this._anAnimationHasStarted) return;
                    this._anAnimationHasStarted = true;
                    this._onStart();
                })
                .onChange((v) => {          
                    for (let j = 0; j < targetIds.length; j++) {
                        const targetId = targetIds[j];
                        const transformDict = initialTransforms![j];
                        const threeEntity = this._threeScene.getObjectByName(`${this._threeEntitySelectorPrefix}${transformInnerGroup ? 'inner_' : ''}${targetId}`);
                        if (!threeEntity) continue;

                        if (type === 'orbit') {
                            const { position: p, quaternion: q } = this._getOrbitEntityValuesByProgress(
                                angle! * v,  
                                animationData, 
                                transformDict
                            );
                            threeEntity.quaternion?.set(q.x, q.y, q.z, q.w);
                            threeEntity.position?.set(p.x, p.y, p.z);
                            return;
                        }

                        const value = this._getTransformEntityValuesByProgress(
                            v, 
                            animationData, 
                            transformDict, 
                            type
                        );

                        //if (this._debug) console.log('presetAnimationController: onChange', value);

                        threeEntity?.[type].set(...value)
                    }         
                }
       
            )
            .onFinish(() => {
                if (this._debug) console.log('presetAnimationController: onFinish', this._animationData);
                this._runningAnimationNumber--
                if (this._runningAnimationNumber === 0) {
                    this._anAnimationHasStarted = false;
                    this._onFinishAll()
                }
            }).start();
            this._runningAnimationNumber++
        }
    }

    private _resetAnimationsWithAnimationData = (data: IAnimationData[]) => {
        for (let i = 0; i < data.length; i++) {
            const animationData = data[i];
            const { settings, type, angle, initialTransforms, transformInnerGroup, targetIds, animation } = animationData;
            animation.set(settings).reset()      
        }
    }

    start (): PresetAnimationController {
        if (this._debug) console.log('presetAnimationController: start', this._animationData);
        if (!this._animationData) return this;
        this._startAnimationsWithAnimationData(this._animationData);
        return this;
    }

    startAllOnloadAnimationsBySceneId(sceneId: string): void {
        if (!this._animationData) return;
        for (let i = 0; i < this._animationData.length; i++) {
            const {parentSceneId, triggerType} = this._animationData[i];
            if (triggerType !== ITriggerTypes.onLoad || parentSceneId !== sceneId) continue;
            this._startAnimationsWithAnimationData([this._animationData[i]])
        }
    }

    resetAllAnimationsBySceneId(sceneId: string): void {
        if (this._debug) console.log('start scene entity animations: ', sceneId)
        if (!this._animationData) return;
        for (let i = 0; i < this._animationData.length; i++) {
            const {parentSceneId, animation} = this._animationData[i];
            if (parentSceneId !== sceneId) continue;
            animation.reset();
        }
    }

    tick(): void {
        if (!this._animationData) return;
        for (let i = 0; i < this._animationData.length; i++) {
            const animationData = this._animationData[i];
            animationData.animation.tick();
        }
    }

    startAllAnimationsByEntityIdAndTrigger(entityId: string, trigger: ITriggerTypes, sceneId: string) {
        if (!this._animationData) return this;
        for (let i = 0; i < this._animationData.length; i++) {
            const {componentId,  parentSceneId, triggerType} = this._animationData[i];
            if (componentId !== entityId || trigger !== triggerType || parentSceneId !== sceneId) continue;
            this._startAnimationsWithAnimationData([this._animationData[i]])
        }
        return this
    }

    resetAllAnimationsByentityIdAndTrigger(entityId: string, trigger: ITriggerTypes, sceneId: string) {
        if (!this._animationData) return this;
        for (let i = 0; i < this._animationData.length; i++) {
            const {componentId,  parentSceneId, triggerType} = this._animationData[i];
            if (componentId !== entityId || trigger !== triggerType || parentSceneId !== sceneId) continue;
            this._resetAnimationsWithAnimationData([this._animationData[i]])
        }
        return this;
    }

    onStart (cb: ICallback): PresetAnimationController {
        if (this._debug) console.log('presetAnimationController: onStart', this._animationData);
        this._onStartCbs.push(cb);
        return this;
    }

    onFinishAll(cb: ICallback): PresetAnimationController {
        if (this._debug) console.log('presetAnimationController: onFinishAll', this._animationData);
        this._onFinishAllCbs.push(cb);
        return this;
    }

    private _onStart () {
        if (this._debug) console.log('presetAnimationController: _onStart', this._animationData);
        this._onStartCbs.forEach(cb => cb())
    }

    private _onFinishAll() {
        if (this._debug) console.log('presetAnimationController: _onFinishAll', this._animationData);
        this._onFinishAllCbs.forEach(cb => cb())
    }

    private _getOrbitEntityValuesByProgress (angle: number, animationData: IAnimationData, initialTransforms: ITransforms) {
        const { pivot: pivotVector3, positionIsOffset, reusableQuaternion, axis } = animationData;
        const pivot = (!positionIsOffset? pivotVector3! : this._threeScene.getObjectByName(`${this._threeEntitySelectorPrefix}${animationData.pivot}`)!.position) as Vector3;
        return rotateAroundWorldAxis(
            reusableQuaternion!,
            pivot,
            initialTransforms!.quaternion!,
            initialTransforms!.position!,
            axis!,
            angle,
            !!positionIsOffset,
            true
        );
    }

    private _getTransformEntityValuesByProgress (v: number, animationData: IAnimationData, initialTransforms: ITransforms, property: ITransformTypes): ITuple3 {
        const s = initialTransforms![property]!.toArray() as ITuple3;

        let e: ITuple3;

        switch (property) {
            case 'rotation': {
                const r = animationData.rotation!;
                e = [r[0] * this._rotation360InRad, r[1] * this._rotation360InRad, r[2] * this._rotation360InRad];
                if (this._debug) console.log('presetAnimationController: rotation change', s, e);
                break;
            }
            case 'position' : {
                if (animationData.isOffset) {
                    const offset = animationData.position!;
                    e = [s[0] + offset[0], s[1] + offset[1], s[2] + offset[2]];
                    if (this._debug) console.log('presetAnimationController: position offset change', s, e);
                } else {
                    e = animationData.position!;
                    if (this._debug) console.log('presetAnimationController: position', s, e);
                }
                break;
            }
            case 'scale' : {
                const m = animationData.multiplier!;
                e = [s[0] * m, s[1] * m, s[2] * m];
                if (this._debug) console.log('presetAnimationController: scale change', s, e);
                break;
            }
            default: {
                e = animationData.position!;
                if (this._debug) console.log('presetAnimationController: DEFAULT change', s, e);
                break
            }
    
        }

        const d = [e[0] - s[0], e[1] - s[1], e[2] - s[2]];
        return [s[0] + d[0] * v, s[1] + d[1] * v, s[2] + d[2] * v] as ITuple3;
    }

    private _getAnimationDataFromOptions(opt: IPresetAnimationControllerOption[]) {
        const animationDataArray: IAnimationData[] = [];
        const options = opt ?? [];

        for (let i = 0; i < options.length; i++) {
            const option = options[i];
            const settings = this._createAnimationSettingsFromOptions(option);
            const transformInnerGroup = option.entityAnimation === IPresetAnimation.spin || option.entityAnimation === IPresetAnimation.scale;
            
            const isPositionOffset = typeof (option as IPresetAnimationOrbitOptions).pivot === 'string';

            const initialTransformValuesArray = this._threeEntitySelectorPrefix ? this._getInitialJuteEntityTransforms(option.targetIds ?? []) : getInitialEntityTransformValuesByIds({ ids: option.targetIds ?? [], componentsById: this._componentsById });
            
            if (!initialTransformValuesArray) return [];
            const { pivot, transformValueArray: initialTransforms } = this._getAnimationValuesByOption(
                option, 
                transformInnerGroup,
                initialTransformValuesArray,
                isPositionOffset
            );

            const animation =  new Transition<number>({ start: 0, end: 0, duration: 0, easingType: IEasingTypes.linear, loopCount: 1 });
        
            const animationData: IAnimationData = { 
                settings, 
                transformInnerGroup, 
                initialTransforms, 
                targetIds: option.targetIds,
                triggerType: option.triggerType,
                parentSceneId: option.parentSceneId,
                componentId: option.componentId,
                animation: animation.set(settings),
                type: this._convertEntityAnimationTypes(option.entityAnimation),
            };

            switch (option.entityAnimation) {
                case IPresetAnimation.scale: {
                    animationData.multiplier = option.multiplier;
                    break;
                }
                case IPresetAnimation.spin: {
                    animationData.rotation = option.rotation;
                    break;
                }
                case IPresetAnimation.move: {
                    animationData.position = this._adjustSpatialPositions ? [option.position[0]/2, option.position[1]/2, option.position[2]/2] : option.position;
                    animationData.isOffset = option.isOffset;
                    break;
                }
                case IPresetAnimation.orbit: {
                    animationData.positionIsOffset = isPositionOffset;
                    animationData.reusableQuaternion = new Quaternion();
                    animationData.angle = option.angle;
                    animationData.axis = new Vector3(...option.axis);
                    animationData.pivot = !isPositionOffset ? pivot : option.pivot as string;
                    if (this._adjustSpatialPositions && !isPositionOffset && typeof animationData.pivot === 'object' && animationData.pivot.x) { 
                        animationData.pivot.set(animationData.pivot.x/2, animationData.pivot.y/2, animationData.pivot.z/2);
                    }
                    break;
                }
            
                default:
                    break;
            }
            
            animationDataArray.push(animationData)
        }

        return animationDataArray;
    }

    private _convertEntityAnimationTypes = (type: IPresetAnimation): 'rotation' | 'position' | 'scale' | 'orbit' => {
        switch (type) {
            case IPresetAnimation.move:
                return 'position';
            case IPresetAnimation.scale:
                return 'scale';
            case IPresetAnimation.spin:
                return 'rotation';
            default:
                return 'orbit';
        }
    };

    private _resetAnimatedObjectsToInitialThreeValues (input: IInternalResetInput) {
        const {animationDataArray, checkResetOnFinish} = input;
        if (!animationDataArray) return;
        for (let i = 0; i < animationDataArray.length; i++) {
            const { targetIds, settings } = animationDataArray[i];
            if (checkResetOnFinish && !settings.resetOnFinish) continue;
            for (let i = 0; i < targetIds.length; i++) {
                const targetId = targetIds[i];
                const { position, rotation } = getInitialEntityTransformValuesByIds({ids: [targetId], componentsById: this._componentsById})![0]
                
                const innerThreeGroup = this._threeScene.getObjectByName(`${this._threeEntitySelectorPrefix}inner_${targetId}`);
                const outerThreeGroup = this._threeScene.getObjectByName(`${this._threeEntitySelectorPrefix}${targetId}`);

                innerThreeGroup?.scale?.set(1, 1, 1);
                innerThreeGroup?.position?.set(0, 0, 0);
                innerThreeGroup?.rotation?.set(0, 0, 0);
        
                outerThreeGroup?.position?.set(...position);
                outerThreeGroup?.rotation?.set(...rotation);
            }
        }
    }

    private _createAnimationSettingsFromOptions = (options: IPresetAnimationControllerOption) => {
        switch (options.entityAnimation) {
            case IPresetAnimation.move:
            case IPresetAnimation.scale:
                return {
                    from: 0,
                    to: 1,
                    duration: options.duration,
                    delay: options.delay,
                    loopCount: options.loopCount,
                    easing: options.easing as any as IEasingTypes,
                    backAndForth: options.backAndForth,
                    resetOnFinish: options.resetOnFinish,
                };
            case IPresetAnimation.orbit:
            case IPresetAnimation.spin:
            default:
                return {
                    from: 0,
                    to: 1,
                    duration: options.duration,
                    delay: options.delay,
                    loopCount: options.loopCount,
                    easing: options.easing as any as IEasingTypes,
                    backAndForth: options.backAndForth,
                };
        }
    };

    private _getAnimationValuesByOption = (
        option: IPresetAnimationOptionUnion, 
        transformInnerGroup: boolean, 
        initialTransformValuesArray: InitialTransformValues[],
        positionIsOffset: boolean,
        
    ) => {
        let pivot: Vector3;
        const optionTargetIdsWithThreeEntityFound = option.targetIds.filter(id => {
            const threeEntity = transformInnerGroup ? this._threeScene.getObjectByName(`${this._threeEntitySelectorPrefix}inner_${id}`) : this._threeScene.getObjectByName(`${this._threeEntitySelectorPrefix}${id}`);
            return !!threeEntity;
        })
        const transformValueArray = optionTargetIdsWithThreeEntityFound.map((id, i) => {
            const threeEntity = transformInnerGroup ? this._threeScene.getObjectByName(`${this._threeEntitySelectorPrefix}inner_${id}`) : this._threeScene.getObjectByName(`${this._threeEntitySelectorPrefix}${id}`);
            const { position: _position } = initialTransformValuesArray[i];
            let position = new Vector3(..._position);
    
            if (option.entityAnimation === IPresetAnimation.orbit) {
                if (positionIsOffset) {
                    pivot = this._threeScene.getObjectByName(`${this._threeEntitySelectorPrefix}${option.pivot}`)!.position.clone();
                    position = position.sub(pivot);
                } else {
                    pivot = new Vector3(...(option.pivot as ITuple3));
                }
            }
    
            return {
                position,
                scale: new Vector3(1, 1, 1),
                rotation: new Euler(0, 0, 0),
                quaternion: threeEntity!.quaternion.clone(),
            };
        });
        return {pivot: pivot!, transformValueArray}
    }

    private _getIsEntityScreenRelativeById(id: string): boolean {
        let isScreenRelative = false;
        if (!this._componentsById || !this._rootId) return isScreenRelative;
        const root = this._componentsById[this._rootId] as IRoot;
        for (let i = 0; i < root.children.length; i++) {
            const sceneId = root.children[i];
            const screenContentId = getScreenContentIdForScene(this._componentsById[sceneId] as ISceneComp, this._componentsById)
            if (!screenContentId) continue;
            const screenRelativeIds = getScreenRelativeComponentIdsByScreenContentId(screenContentId, this._componentsById);
            if (screenRelativeIds.includes(id)) {
                isScreenRelative = true;
                break;
            }
        }  
        return isScreenRelative;
    }

    private _getInitialJuteEntityTransforms(ids: string[]): InitialTransformValues[] {
        if (!this._componentsById) return [];
        return ids.map(id => {
            const { scale: s = [1, 1, 1], position: p = [0, 0, 0], rotation: r = [0, 0, 0], type } = this._componentsById![id] as ISpatialComponentUnion ?? {};
            const isScreenRelative = this._getIsEntityScreenRelativeById(id);
            const scale = getAdjustedScale(this._mode as any, isScreenRelative, s as ITuple3);
            const adjustedPosition = getAdjustedPosition(this._mode as any, isScreenRelative, p as ITuple3)
            const rotation = [MathUtils.degToRad(r[0]), MathUtils.degToRad(r[1]), MathUtils.degToRad(r[2])] as ITuple3;
            const position = type === IComponentType.Emitter ? [0, 0, 0] : adjustedPosition ?? [0, 0, 0];
            return {
                scale: [...scale] as ITuple3,
                position: [...position] as ITuple3,
                rotation: [...rotation] as ITuple3
            };
        })
    }

    get hasNonResetAnimation (): boolean {
        if (!this._animationData) return false;
        return this._animationData.filter(data => !data.settings.resetOnFinish).length > 0;
    }

    get hasLoopedAnimation (): boolean {
        if (!this._animationData) return false;
        return this._animationData.filter(data => !data.settings.loopCount === null).length > 0;
    }

    get hasAnimationWithDifferentStartToEndValues (): boolean {
        if (!this._animationData) return false;

        return this._animationData.filter(data => {
            return data.type === 'orbit' && data.angle !== MathUtils.degToRad(360) ||
                ((data.type === 'position' || data.type === 'scale')  && (!!data.settings.loopCount && data.settings.loopCount % 2 !== 0) && !data.settings.resetOnFinish)
        }).length > 0;
    }
}